import { Formik } from 'formik';
import React from 'react';
import {
  Container,
  Divider,
  Form,
  Header,
  Button,
  Message,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'types/provider';
import { request } from 'utils/api';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import InputField from 'components/form-fields/formik/InputField';
import SelectField from 'components/form-fields/formik/SelectField';
import styles from './Settings.module.css';

const SettingsProductProposition: React.FC<{
  provider: Partial<Provider>;
}> = ({ provider }) => {
  const { t } = useTranslation();
  return (
    <Container>
      <Header as="h2" style={{ padding: '0', margin: '0' }}>
        {t('editSandboxProductProposition.title', 'Product Proposition')}
      </Header>
      <Divider />
      <p>
        {t(
          'editSandboxProductProposition.description',
          'Product proposition settings'
        )}
      </p>
      <Formik
        enableReinitialize
        initialValues={{
          enableAdvancedTariffs: provider.enableAdvancedTariffs,
          enableMSPLimits: provider.enableMSPLimits,
          enableBulkOperations: provider.enableBulkOperations,
          enableWhitelabelFees: provider.enableWhitelabelFees,
          enableMspFees: provider.enableMspFees,
        }}
        onSubmit={(values) => {
          request({
            method: 'PATCH',
            path: `/1/sandboxes/${(provider as any)._id}/settings`,
            body: {
              ...provider,
              ...values,
            },
          });
        }}>
        {({ handleSubmit, isSubmitting, isValid, dirty }) => {
          return (
            <Form>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxProductProposition.enableAdvancedTariffs',
                    'Enable Advanced Tariff Support (scheduled, idle, dynamic)'
                  )}
                  name="enableAdvancedTariffs"
                />
                <Message info>
                  {t(
                    'editSandboxProductProposition.enableAdvancedTariffsInfo',
                    'Unlocks sophisticated tariff structures including time-based scheduling, idle fees, and dynamic pricing based on demand or grid conditions.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxProductProposition.enableMSPLimits',
                    'Enable MSP Limits'
                  )}
                  name="enableMSPLimits"
                />
                <Message info>
                  {t(
                    'editSandboxProductProposition.enableMSPLimitsInfo',
                    'Allows setting usage limits and restrictions for Mobility Service Provider (MSP) accounts to control access and costs.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxProductProposition.enableBulkOperations',
                    'Enable Charge Station Bulk Operations'
                  )}
                  name="enableBulkOperations"
                />
                <Message info>
                  {t(
                    'editSandboxProductProposition.enableBulkOperationsInfo',
                    'Enables batch operations for managing multiple charging stations simultaneously, improving operational efficiency.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxProductProposition.enableCustomFees',
                    'Enable Custom Fees'
                  )}
                  name="enableWhitelabelFees"
                />
                <Message info>
                  {t(
                    'editSandboxProductProposition.enableWhitelabelFeesInfo',
                    'Allows providers to configure custom fee structures and additional charges beyond standard energy costs.'
                  )}
                </Message>
              </Form.Field>
              <Form.Field className={styles.productProposition}>
                <CheckboxField
                  toggle
                  label={t(
                    'editSandboxProductProposition.enableMspFees',
                    'Enable MSP Fees'
                  )}
                  name="enableMspFees"
                />
                <Message info>
                  {t(
                    'editSandboxProductProposition.enableMspFeesInfo',
                    'Enables fee structures specific to Mobility Service Providers, including transaction fees and service charges.'
                  )}
                </Message>
              </Form.Field>
              <Button
                disabled={isSubmitting || !isValid}
                onClick={() => handleSubmit()}
                primary>
                {t('save', 'Save')}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default SettingsProductProposition;
